/**
 * The Transit section's chrome, laid out like the Bridge Figma file: a fixed
 * side nav sitting on the page background, and the page itself in a white
 * rounded panel beside it. Transit's own shell rather than an AppShell variant:
 * Bridge has no icon rail, top bar or tabs, and AppShell's hover-expand rail
 * would fight the always-open nav.
 */
import type { ReactNode } from 'react'
import { Link, NavLink } from 'react-router-dom'
import type { NavItem } from '../ui/AppShell'
import { cn } from '../../lib/utils'

interface TransitShellProps {
  nav: NavItem[]
  homeTo: string
  /** Below the nav: the org's transitions. */
  sidebarExtra?: ReactNode
  /** Pinned to the bottom of the nav: the user menu. */
  footer?: ReactNode
  children: ReactNode
}

const TransitShell = ({ nav, homeTo, sidebarExtra, footer, children }: TransitShellProps) => (
  <div className="flex h-screen w-full min-w-0 bg-background">
    <aside className="flex w-[277px] shrink-0 flex-col bg-sidebar py-3">
      <div className="flex min-h-0 flex-1 flex-col gap-6 px-2">
        <Link to={homeTo} aria-label="Transit home" className="w-fit px-1">
          <img src="/o_logo_navy.png" alt="Overmatch" className="block h-7 w-auto dark:hidden" />
          <img src="/o_logo_white.png" alt="" aria-hidden className="hidden h-7 w-auto dark:block" />
        </Link>

        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto">
          <nav>
            {nav.map((item) => {
              const Icon = item.icon
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  // The home item is New Transition, the tour's first step.
                  data-tour={item.to === homeTo ? 'new-transition' : undefined}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 rounded-md p-3 text-base leading-[18px] text-sidebar-foreground transition-colors',
                      isActive ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'hover:bg-sidebar-accent/60'
                    )
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              )
            })}
          </nav>
          {sidebarExtra}
        </div>
      </div>

      {footer && <div className="shrink-0 px-3 pt-3">{footer}</div>}
    </aside>

    <main className="flex min-w-0 flex-1 py-6 pl-3 pr-6">
      <div className="min-w-0 flex-1 overflow-auto rounded-xl border border-border bg-card shadow-xs">
        {children}
      </div>
    </main>
  </div>
)

export default TransitShell
